// The HedgeMatrix host adapter for the SELECT COPILOT (CR-20260804-001, the two-copilots
// reversal - ruling AF). The embedded OpgCopilotComponent talks to its host ONLY through
// IOpgCopilotFacade; this class implements that contract over the Hedge Matrix, so the
// LITERAL same copilot serves two pop-ups. Every member that has no Matrix equivalent is a
// DOCUMENTED no-op, never a silent one - the decisions are written at the member.
//
// HARD RULE (the book is not "loaded strategies"): hasAnyLoadedStrategies / clearAllStrategies
// operate STRICTLY on copilot-loaded previews tracked in this adapter's own registry. The
// trader's held hedges are positions; the warned clear-on-run must never wipe a real book.

import {IOpgCopilotFacade, OpgEsContractCandidate} from '../../copilot-shared/opg-copilot-facade';
import {OpgSide, OpgTradeContent} from '../../copilot-shared/opg-copilot-domain';
import {OptionExpirationDescriptor} from '../../shell-communication/shell-dto-protocol';
import {TradingInstrument} from '../../trading-instruments/trading-instrument.class';
import {PricingGridLegDescriptor} from '../../options-pricing-grid/model/pricing-grid-leg.descriptor';
import {findOffsetExpiration} from '../hedge-matrix/hedge-matrix-data.service';
import {
    makeOptionTicker,
    makeOptionTickerDisplayName,
    parseOptionTicker
} from '../../options-common/options.model';
import {Leg, PackageCard, parseLegActionType} from './copilot-domain';
import {ApplyResult} from './copilot-facade';
import {getShortUUID, isValidNumber} from '../../utils';

// What the adapter needs from its host (HgCopilotComponent implements it inline). Kept as an
// interface so the adapter never reaches into the component past these reads/effects.
export interface ISelectCopilotHost {
    entitled(): boolean;
    // VISIBLE-filtered chain descriptors (the OPG contract: non-visible expirations must never
    // leak into the date block or the skill context).
    expirationDescriptors(): OptionExpirationDescriptor[];
    // The HM "current date" (owner rule 2026-08-04): nearest hedge expiration, else nearest
    // chain expiration - this panel's equivalent of OPG's loaded expiration.
    currentDateKey(): string | null;
    instrument(): TradingInstrument | null;
    underlying(): string | null;
    chosenAtm(): number | null;      // the Matrix's own center strike (the 'chosen' anchor)
    dynamicAtm(): number | null;     // the live market ATM (the 'market' anchor)
    defaultQty(): number | undefined;
    presetApgSource?(): string | null;
    // Preview effects - implemented over ctx.applySolution/removeCopilotLegs with the same
    // toast + stale-suppression wrapper the Adjust cards use.
    applyPreview(solutionId: string, legs: Leg[], cards: PackageCard[]): Promise<ApplyResult>;
    removePreview(solutionId: string): Promise<void>;
    // An async preview FAILED after loadTrade already returned its id - the host routes this
    // to the embedded copilot's onHostStrategyRemoved so the card's Previewed state reverts
    // ((S8) consistency hook - the same path a grid-side removal takes in OPG).
    previewLost(solutionId: string): void;
    // ---- ES mode (CR-20260924-002) — see the IOpgCopilotFacade ES members ----
    esMode(): boolean;
    esContracts(): OpgEsContractCandidate[] | null;
    // The VISIBLE expirations of one ES contract's chain (empty until fetched).
    esExpirationDescriptors(contract: string): OptionExpirationDescriptor[];
    matrixEsHedgingEnabled(): boolean;
    normFactor(asset: string | null): number;
}

export class HmSelectCopilotFacade implements IOpgCopilotFacade {
    readPresetApgSource(): string | null { return this._host.presetApgSource ? this._host.presetApgSource() : null; }

    constructor(private readonly _host: ISelectCopilotHost) {
    }

    // Copilot-loaded preview ids ONLY (ES5: array + indexOf, the family idiom).
    private readonly _loaded: string[] = [];

    // ---- entitlement -----------------------------------------------------------------------

    isOpgCoPilotAvailable(): boolean {
        return this._host.entitled();
    }

    // ---- reads -----------------------------------------------------------------------------

    readAvailableExpirations(): OptionExpirationDescriptor[] {
        return this._host.expirationDescriptors() || [];
    }

    readSelectedExpirationDate(): string | null {
        return this._host.currentDateKey();
    }

    // CR-20260827-004: cards price at the trade's OWN date. Delegates to the same resolver
    // buildLegs places the preview column with, so a card's price and its previewed hedge
    // can never disagree on date. (The old member resolved from currentDateKey — the nearest
    // expiration — which priced every Select card at today's options: the reported bug.)
    readExpirationForTradeDate(dateKey: string | null, offset: number | null, esContract?: string | null): OptionExpirationDescriptor | null {
        return this.resolveLegExpiration(dateKey, offset, esContract || null);
    }

    // ---- ES mode (CR-20260924-002) ---------------------------------------------------------
    // The trader keeps typing SPX (readAvailableExpirations / readStrikeLadder stay the SPX
    // chain); the DATE block lists the ES chain; the backend lens returns every trade on the ES
    // contract; the card and the preview resolve that trade's legs on ITS contract's chain.

    readEsMode(): boolean {
        return this._host.esMode();
    }

    readEsContracts(): OpgEsContractCandidate[] | null {
        return this._host.esContracts();
    }

    // The union of the candidates' visible ES chains, NEAREST CONTRACT FIRST — a date two
    // contracts list keeps the nearer one's descriptor, the same first-listing rule the backend
    // lens applies — sorted by date. Null off ES mode: the date block reads the SPX chain.
    readDateExpirations(): OptionExpirationDescriptor[] | null {
        if (!this._host.esMode()) {
            return null;
        }
        const candidates = this._host.esContracts() || [];
        const seen: { [k: string]: boolean } = {};
        const out: OptionExpirationDescriptor[] = [];
        for (let i = 0; i < candidates.length; i++) {
            const list = this._host.esExpirationDescriptors(candidates[i].contract) || [];
            for (let j = 0; j < list.length; j++) {
                const key = list[j].optionExpirationDate;
                if (!key || seen[key]) {
                    continue;
                }
                seen[key] = true;
                out.push(list[j]);
            }
        }
        out.sort((a, b) => a.optionExpirationDate < b.optionExpirationDate ? -1
            : (a.optionExpirationDate > b.optionExpirationDate ? 1 : 0));
        return out;
    }

    // An ES card cannot land while the Hedge Matrix's own ES hedging is off: its ES columns are
    // not built, so the hedge would exist with nowhere to show (owner ruling 2026-09-24: run
    // allowed, preview blocked). SPX cards are never blocked here.
    readPreviewBlockedReason(side: OpgSide, content: OpgTradeContent): string | null {
        if (!content || !content.esContract) {
            return null;
        }
        return this._host.matrixEsHedgingEnabled()
            ? null
            : 'Enable ES Hedging on the Hedge Matrix to Preview an ES Hedge';
    }

    readLegNormFactor(asset: string | null): number {
        const factor = this._host.normFactor(asset);
        return isValidNumber(factor, true) ? factor : 1;
    }

    // 0 = unresolved (no Matrix loaded yet). The OPG contract returns a number and the wire
    // carries a non-nullable double; a run cannot meaningfully fire before a portfolio/chain
    // exists (entitlement + instrument gates), so 0 never reaches a live request as an anchor.
    readChosenAtm(): number {
        const v = this._host.chosenAtm();
        return isValidNumber(v, true) ? v : 0;
    }

    readDynamicAtm(): number {
        const v = this._host.dynamicAtm();
        return isValidNumber(v, true) ? v : 0;
    }

    // The skill-context ladder ("what the trader sees"). Here: the nearest visible expiration's
    // strikes - the same universe the strike pickers use. INERT server-side by the OPG engine's
    // own rule ("every strike, wing, snap and boundary comes from the backend chain - never
    // from the client StrikeLadder"), so this read cannot move results.
    readStrikeLadder(): number[] {
        const list = this.readAvailableExpirations();
        let nearest: OptionExpirationDescriptor | null = null;
        for (let i = 0; i < list.length; i++) {
            if (!nearest || list[i].optionExpirationDate < nearest.optionExpirationDate) {
                nearest = list[i];
            }
        }
        return nearest && nearest.strikes ? nearest.strikes : [];
    }

    readDefaultQty(): number {
        const v = this._host.defaultQty();
        return isValidNumber(v, true) ? v : 0;
    }

    readSymbol(): string | null {
        return this._host.underlying();
    }

    readTradingInstrument(): TradingInstrument | null {
        return this._host.instrument();
    }

    // ---- trade loading (the Matrix preview: a new hedge column per trade) --------------------

    // The mold contract (anchor-first): legs[0] sits ON content.startingStrike and each 'width'
    // entry advances the strike cursor OUTWARD - UP for calls, DOWN for puts (the same walk the
    // OPG grid performs; its call-side array reversal is display-order plumbing, not the mold).
    // Wire leg qty = Units x m (the backend mapper's law); the FE multiplies by the grid's
    // Default Qty AT LOAD - mirrored here, so the previewed hedge holds exactly the quantity
    // the card displays. An unresolvable trade (no chain / no default qty / a bad ticker)
    // refuses WHOLE - all-or-none, nothing partial ever lands on the Matrix.
    loadTrade(side: OpgSide, content: OpgTradeContent): string {
        const strategyId = getShortUUID();
        const legs = this.buildLegs(side, content);
        if (!legs || legs.length === 0) {
            // Refused before anything touched the Matrix. loadTrade's sync contract has no
            // failure channel, so report the id and immediately revoke it through the same
            // consistency hook an async failure uses.
            this.schedulePreviewLost(strategyId);
            return strategyId;
        }
        const cards: PackageCard[] = [{title: content.name, legs: legs}];
        this._loaded.push(strategyId);
        this._host.applyPreview(strategyId, legs, cards).then(result => {
            if (!result || !result.applied) {
                this.forget(strategyId);
                this._host.previewLost(strategyId);
            }
        }).catch(() => {
            this.forget(strategyId);
            this._host.previewLost(strategyId);
        });
        return strategyId;
    }

    unloadTrade(side: OpgSide, strategyId: string): void {
        this.forget(strategyId);
        this._host.removePreview(strategyId).catch(() => { /* best-effort un-preview */ });
    }

    // ---- borders ("X marks the spot") -------------------------------------------------------
    // NO-OPS by design: the Matrix has no strike-border registry - the previewed hedge COLUMN
    // is the visual. The orchestrator's border bookkeeping stays harmless against these.

    setBorder(strategyId: string, side: OpgSide, strike: number): void {
    }

    clearBorder(strategyId: string): void {
    }

    clearAllBorders(): void {
    }

    // ---- pricing + live-apply ---------------------------------------------------------------

    // null = "no grid price wins here": the trade cards price themselves from the live quote
    // cache, and the Matrix column's own pricing is its own surface - there is no single cell
    // that IS this trade's tradeable reference the way an OPG grid cell is.
    readLoadedTradePrice(side: OpgSide, strategyId: string, strike: number): number | null {
        return null;
    }

    // NO-OP by design: the Matrix has no per-side Live toggle to honour.
    applySideIfLive(side: OpgSide): void {
    }

    // ---- warned clear-on-run ----------------------------------------------------------------
    // PREVIEWS ONLY - NEVER the book (the hard rule at the top of this file).

    hasAnyLoadedStrategies(): boolean {
        return this._loaded.length > 0;
    }

    clearAllStrategies(): void {
        const ids = this._loaded.slice();
        this._loaded.length = 0;
        for (let i = 0; i < ids.length; i++) {
            this._host.removePreview(ids[i]).catch(() => { /* best-effort */ });
        }
    }

    // ---- cross-date preview navigation ------------------------------------------------------

    // TRUE no-op: the Hedge Matrix displays every expiration at once, so a trade bound to
    // another date needs no panel navigation. The orchestrator's cross-date auto-un-preview
    // has already run by the time this is called; accepting is correct on this host.
    navigateToExpiration(dateKey: string): boolean {
        return true;
    }

    // ---- internals --------------------------------------------------------------------------

    // The chain a trade's legs live on: its ES contract's when it names one (CR-20260924-002),
    // else the SPX chain. The card's contract decides, never the toggle's current state.
    private chainFor(esContract: string | null): OptionExpirationDescriptor[] {
        return esContract
            ? (this._host.esExpirationDescriptors(esContract) || [])
            : this.readAvailableExpirations();
    }

    private descriptorFor(dateKey: string | null, esContract: string | null): OptionExpirationDescriptor | null {
        if (!dateKey) {
            return null;
        }
        const list = this.chainFor(esContract);
        for (let i = 0; i < list.length; i++) {
            if (list[i].optionExpirationDate === dateKey) {
                return list[i];
            }
        }
        return null;
    }

    private buildLegs(side: OpgSide, content: OpgTradeContent): Leg[] | null {
        // CR-20260827-002 (contract v5): the engine's leg quantities are ABSOLUTE contract
        // counts — what will execute — so no × defaultQty here any more, and a missing Default
        // Qty no longer blocks the preview (a normalized Run was already refused earlier, at
        // the wire-mapping seam, which is the right surface for that).
        const optionType = side === 'calls' ? 'Call' : 'Put';
        const direction = side === 'calls' ? 1 : -1;
        const molds: PricingGridLegDescriptor[] = content.legs || [];
        const legs: Leg[] = [];
        // CR-20260924-002: an ES trade already carries ES strikes and ES quantities (the backend
        // lens did that); its tickers are built on its contract's chain, and each leg remembers
        // the gap the lens used so the matrix can create a missing ES silo on the same number.
        const esContract = content.esContract || null;
        const esGap = esContract && isValidNumber(content.esGap) ? content.esGap : null;
        let strike = content.startingStrike;
        for (let i = 0; i < molds.length; i++) {
            const entry = molds[i];
            if (!entry) {
                return null;
            }
            if (entry.type === 'width') {
                if (!isValidNumber(entry.width, true)) {
                    return null;
                }
                strike += direction * entry.width;
                continue;
            }
            const exp = this.resolveLegExpiration(content.expiration, entry.expirationOffset, esContract);
            if (!exp) {
                return null;
            }
            const ticker = makeOptionTicker(exp, optionType, strike);
            if (!ticker) {
                return null;
            }
            const parsed = parseOptionTicker(ticker);
            if (!parsed) {
                return null;
            }
            const action = entry.side === 'Sell' ? 'Sell To Open' : 'Buy To Open';
            const parts = parseLegActionType(action);
            if (!parts) {
                return null;
            }
            legs.push({
                quantity: entry.qty || 1,
                ticker: ticker,
                tickerDisplayName: makeOptionTickerDisplayName(parsed),
                action: action,
                side: parts.side,
                effect: parts.effect,
                asset: parsed.underlying,
                esGap: esContract ? esGap : undefined
            });
        }
        return legs;
    }

    private resolveLegExpiration(baseKey: string | null, offset: number | null | undefined,
                                 esContract: string | null): OptionExpirationDescriptor | null {
        const base = this.descriptorFor(baseKey, esContract);
        if (!base) {
            return null;
        }
        if (!isValidNumber(offset, true) || offset === 0) {
            return base;
        }
        return findOffsetExpiration(base, offset, {expirations: this.chainFor(esContract)} as any) || null;
    }

    private forget(strategyId: string): void {
        const at = this._loaded.indexOf(strategyId);
        if (at >= 0) {
            this._loaded.splice(at, 1);
        }
    }

    // The sync-refusal path fires the consistency hook on a fresh microtask, so the
    // orchestrator has finished registering the trade before it is revoked.
    private schedulePreviewLost(strategyId: string): void {
        Promise.resolve().then(() => this._host.previewLost(strategyId));
    }
}
